
import { SPFI, spfi } from "@pnp/sp";
import "@pnp/sp/webs";
import "@pnp/sp/lists";
import "@pnp/sp/fields";
import "@pnp/sp/items";
import "@pnp/sp/batching";
import { LogLevel } from "@pnp/logging";
import LoggingService from "../LoggingService";
import { BrowserFetchWithRetry } from "@pnp/queryable";
import { msalInstance, tokenRequest } from "@/msalAuth";
import * as msalAuth from "@/msalAuth"; // Import all exports from msalAuth
import { PnPjsServiceBase } from "./PnPjsServiceBase";
import sharePointService from "@/services/sharePointService";
import { initialize, testConnection } from "@/services/sharePointService";

/**
 * PnPjs service for SharePoint operations
 */
class PnPjsService extends PnPjsServiceBase {
  private static instance: PnPjsService;
  private _sp: SPFI | null = null;
  // Make LOG_SOURCE match the base class property exactly
  protected readonly LOG_SOURCE = "PnPjsService" as const;
  private _siteUrl: string | null = null;
  // Changed from private to protected to match the base class
  protected _isInitialized = false;
  private _useCertificate = false;
  private _certificateThumbprint: string | null = null;
  private _clientId: string | null = null;

  private constructor() {
    super();
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): PnPjsService {
    if (!PnPjsService.instance) {
      PnPjsService.instance = new PnPjsService();
    }
    return PnPjsService.instance;
  }

  /**
   * Initialize the service
   * @param options Configuration options
   */
  public async initialize(options: {
    msalConfig?: {
      clientId: string;
      useCertificate?: boolean;
      certificateThumbprint?: string;
    };
    defaultSiteUrl?: string;
    spfxContext?: any;
  } = {}): Promise<void> {
    try {
      // Only initialize once
      if (this._isInitialized) {
        LoggingService.warn('PnPjs service already initialized', {}, this.LOG_SOURCE);
        return;
      }

      LoggingService.info('Initializing PnPjs service', {
        options
      }, this.LOG_SOURCE);

      // Set site URL if provided
      if (options.defaultSiteUrl) {
        this._siteUrl = options.defaultSiteUrl;
      }

      // Check if we should use certificate-based authentication
      this._useCertificate = options.msalConfig?.useCertificate || false;
      
      if (this._useCertificate && options.msalConfig?.certificateThumbprint) {
        this._certificateThumbprint = options.msalConfig.certificateThumbprint;
        this._clientId = options.msalConfig.clientId;
        
        LoggingService.info('Using certificate-based authentication', {
          certificateThumbprint: this._certificateThumbprint,
          clientId: this._clientId
        }, this.LOG_SOURCE);
      }

      // Handle SPFx context if available
      if (options.spfxContext) {
        LoggingService.info('Using SPFx context for authentication', {}, this.LOG_SOURCE);
        await this.initializeWithSpfxContext(options.spfxContext);
        return;
      }

      // If a site URL is provided, we can initialize PnPjs
      if (this._siteUrl) {
        if (this._useCertificate) {
          // Use certificate-based authentication
          await this.initializeWithCertificate();
        } else {
          // Regular browser-based auth
          await this.initializeWithBrowser();
        }
      } else {
        LoggingService.warn('No site URL provided, PnPjs not fully initialized', {}, this.LOG_SOURCE);
      }

      this._isInitialized = true;
    } catch (error) {
      LoggingService.error('Failed to initialize PnPjs service', error, this.LOG_SOURCE);
      this._isInitialized = false;
      throw error;
    }
  }

  /**
   * Initialize with certificate-based authentication
   */
  private async initializeWithCertificate(): Promise<void> {
    try {
      if (!this._siteUrl) {
        throw new Error('Site URL is required for certificate-based authentication');
      }

      if (!this._certificateThumbprint || !this._clientId) {
        throw new Error('Certificate thumbprint and client ID are required for certificate-based authentication');
      }

      // For certificate-based authentication, we use SharePointService to handle 
      // the authentication flow for now
      localStorage.setItem("sharepoint_use_certificate", "true");
      
      LoggingService.info('Initializing SharePoint service with certificate', {
        siteUrl: this._siteUrl
      }, this.LOG_SOURCE);

      // Let's initialize SharePoint service
      await initialize(this._siteUrl);

      // For now, we'll just set a flag to indicate success
      LoggingService.info('SharePoint service initialized with certificate', {}, this.LOG_SOURCE);
      this._isInitialized = true;
    } catch (error) {
      LoggingService.error('Failed to initialize with certificate', error, this.LOG_SOURCE);
      throw error;
    }
  }

  /**
   * Initialize with browser-based authentication
   */
  private async initializeWithBrowser(): Promise<void> {
    try {
      if (!this._siteUrl) {
        throw new Error('Site URL is required for browser-based authentication');
      }

      // Make sure we have an active account
      msalAuth.setActiveAccountIfNeeded();

      // Check if we have an active account
      const activeAccount = msalInstance.getActiveAccount();
      if (!activeAccount) {
        LoggingService.warn('No active MSAL account, attempting to acquire one', {}, this.LOG_SOURCE);
        
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
        } else {
          // We need to login interactively
          try {
            const result = await msalInstance.loginPopup(tokenRequest);
            msalInstance.setActiveAccount(result.account);
          } catch (loginError) {
            LoggingService.error('Failed to login with MSAL', loginError, this.LOG_SOURCE);
            throw loginError;
          }
        }
      }

      // Try to acquire token silently first
      try {
        await initialize(this._siteUrl);
        const connected = await testConnection();
        
        if (!connected) {
          throw new Error('Failed to connect to SharePoint');
        }
        
        LoggingService.info('Successfully connected to SharePoint', {}, this.LOG_SOURCE);
      } catch (error) {
        LoggingService.error('Failed to initialize with browser', error, this.LOG_SOURCE);
        throw error;
      }
    } catch (error) {
      LoggingService.error('Failed to initialize with browser', error, this.LOG_SOURCE);
      throw error;
    }
  }

  /**
   * Initialize with SPFx context
   * @param context SPFx context
   */
  private async initializeWithSpfxContext(context: any): Promise<void> {
    try {
      if (!context) {
        throw new Error('SPFx context is required');
      }

      // Create SP instance with SPFx context
      this._sp = spfi().using(context.spHttpClient);
      
      // Set the site URL from the context
      if (context.pageContext && context.pageContext.site) {
        this._siteUrl = context.pageContext.site.absoluteUrl;
      }
      
      LoggingService.info('PnPjs initialized with SPFx context', {
        siteUrl: this._siteUrl
      }, this.LOG_SOURCE);
      
      this._isInitialized = true;
    } catch (error) {
      LoggingService.error('Failed to initialize with SPFx context', error, this.LOG_SOURCE);
      throw error;
    }
  }

  /**
   * Get the SP instance
   * @param siteUrl Optional site URL to use
   */
  public async getSP(siteUrl?: string): Promise<SPFI> {
    try {
      // If site URL is provided, update the current one
      if (siteUrl) {
        this._siteUrl = siteUrl;
      }
      
      // If not initialized, initialize now
      if (!this._isInitialized) {
        await this.initialize({
          defaultSiteUrl: this._siteUrl || undefined
        });
      }
      
      // If we're using certificate-based authentication, we need to use SharePointService
      if (this._useCertificate) {
        // Initialize SharePoint service with the site URL
        await initialize(this._siteUrl || "");
        
        // Test the connection
        const connected = await testConnection();
        if (!connected) {
          throw new Error('Failed to connect to SharePoint with certificate');
        }
      }
      
      // Create a new SP instance since we don't store it for certificate auth
      return spfi(this._siteUrl || '')
        .using(BrowserFetchWithRetry())
        .using((options) => {
          options.headers = {
            ...options.headers,
            Accept: "application/json;odata=verbose",
            "Content-Type": "application/json;odata=verbose"
          };
          return options;
        });
    } catch (error) {
      LoggingService.error('Failed to get SP instance', error, this.LOG_SOURCE);
      throw error;
    }
  }

  /**
   * Create a new batch for batch operations
   */
  public createBatch(): any {
    if (!this._sp) {
      throw new Error('SP instance not initialized');
    }
    
    return this._sp.batched();
  }
}

// Export singleton instance
const pnpjsService = PnPjsService.getInstance();

export {
  pnpjsService as PnPjsService,
  pnpjsService as default
};
